/**
 * ✅ FASE 10: Performance Monitor Dashboard
 * Dashboard flutuante para visualizar métricas de Web Vitals em tempo real
 * Visível apenas em DEV mode ou para admins
 */

import { useState, useEffect, memo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { X, Activity, Minimize2, Maximize2 } from 'lucide-react';
import { performanceMonitorService } from '@/services/performanceMonitorService';
import { useAuthStore } from '@/stores/useAuthStore';

export const PerformanceMonitor = memo(() => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [stats, setStats] = useState(performanceMonitorService.getStats());
  const { user } = useAuthStore();

  // Só mostrar em DEV ou para admins
  const shouldShow = import.meta.env.DEV || user?.email?.includes('@admin');

  useEffect(() => {
    if (!shouldShow) return;

    const unsubscribe = performanceMonitorService.subscribe(setStats);
    return unsubscribe;
  }, [shouldShow]);

  if (!shouldShow || !isVisible) {
    return shouldShow ? (
      <Button
        onClick={() => setIsVisible(true)}
        size="sm"
        variant="outline"
        className="fixed bottom-4 right-4 z-50 shadow-lg"
      >
        <Activity className="h-4 w-4" />
      </Button>
    ) : null;
  }

  const score = performanceMonitorService.getPerformanceScore();
  const getScoreColor = (s: number) => {
    if (s >= 90) return 'text-green-600';
    if (s >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getMetricRating = (name: string) => {
    const metric = stats.metrics.find(m => m.name === name);
    if (!metric) return null;
    
    const colors = {
      good: 'bg-green-500',
      'needs-improvement': 'bg-yellow-500',
      poor: 'bg-red-500',
    };
    
    return (
      <div className={`w-2 h-2 rounded-full ${colors[metric.rating]}`} />
    );
  };

  const formatMs = (ms: number) => ms > 0 ? `${Math.round(ms)}ms` : '-';
  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '-';
    const kb = bytes / 1024;
    return kb > 1024 ? `${(kb / 1024).toFixed(1)}MB` : `${kb.toFixed(0)}KB`;
  };

  if (isMinimized) {
    return (
      <Card className="fixed bottom-4 right-4 z-50 shadow-xl w-16 h-16 cursor-pointer"
            onClick={() => setIsMinimized(false)}>
        <CardContent className="p-0 flex items-center justify-center h-full">
          <div className="text-center">
            <Activity className="h-6 w-6 mx-auto mb-1" />
            <span className={`text-xs font-bold ${getScoreColor(score)}`}>
              {score}
            </span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="fixed bottom-4 right-4 z-50 shadow-xl w-96 max-h-[80vh] overflow-auto">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Performance Monitor
          </CardTitle>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMinimized(true)}
              className="h-6 w-6 p-0"
            >
              <Minimize2 className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsVisible(false)}
              className="h-6 w-6 p-0"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4 text-xs">
        {/* Score geral */}
        <div className="text-center p-4 bg-muted rounded-lg">
          <div className={`text-4xl font-bold ${getScoreColor(score)}`}>
            {score}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Performance Score
          </div>
        </div>

        {/* Web Vitals */}
        <div className="space-y-2">
          <h4 className="font-semibold text-xs">Web Vitals</h4>
          
          <div className="grid grid-cols-2 gap-2">
            <MetricItem
              label="FCP"
              value={formatMs(stats.averages.FCP)}
              rating={getMetricRating('FCP')}
              tooltip="First Contentful Paint"
            />
            <MetricItem
              label="LCP"
              value={formatMs(stats.averages.LCP)}
              rating={getMetricRating('LCP')}
              tooltip="Largest Contentful Paint"
            />
            <MetricItem
              label="CLS"
              value={stats.averages.CLS > 0 ? stats.averages.CLS.toFixed(3) : '-'}
              rating={getMetricRating('CLS')}
              tooltip="Cumulative Layout Shift"
            />
            <MetricItem
              label="TTFB"
              value={formatMs(stats.averages.TTFB)}
              rating={getMetricRating('TTFB')}
              tooltip="Time to First Byte"
            />
            <MetricItem
              label="INP"
              value={formatMs(stats.averages.INP)}
              rating={getMetricRating('INP')}
              tooltip="Interaction to Next Paint"
            />
          </div>
        </div>

        {/* Navigation Timing */}
        {stats.navigation && (
          <div className="space-y-2">
            <h4 className="font-semibold text-xs">Navigation</h4>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-muted-foreground">DOM Ready:</span>
                <span className="font-mono">{formatMs(stats.navigation.domContentLoaded)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Load Complete:</span>
                <span className="font-mono">{formatMs(stats.navigation.loadComplete)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Transfer Size:</span>
                <span className="font-mono">{formatBytes(stats.navigation.transferSize)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Type:</span>
                <Badge variant="secondary" className="h-5 text-xs">
                  {stats.navigation.type}
                </Badge>
              </div>
            </div>
          </div>
        )}

        {/* Session info */}
        <div className="pt-2 border-t text-xs text-muted-foreground">
          <div className="flex justify-between">
            <span>Métricas coletadas:</span>
            <span className="font-mono">{stats.metrics.length}</span>
          </div>
          <div className="flex justify-between">
            <span>Tempo de sessão:</span>
            <span className="font-mono">
              {Math.round((Date.now() - stats.sessionStart) / 1000)}s
            </span>
          </div>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={() => performanceMonitorService.reset()}
          className="w-full text-xs"
        >
          Reset Métricas
        </Button>
      </CardContent>
    </Card>
  );
});

PerformanceMonitor.displayName = 'PerformanceMonitor';

// Componente auxiliar para item de métrica
const MetricItem = memo(({ label, value, rating, tooltip }: {
  label: string;
  value: string;
  rating: React.ReactNode;
  tooltip: string;
}) => (
  <div className="flex items-center justify-between p-2 bg-muted rounded">
    <div className="flex items-center gap-1">
      {rating}
      <span className="font-semibold" title={tooltip}>{label}</span>
    </div>
    <span className="font-mono text-xs">{value}</span>
  </div>
));

MetricItem.displayName = 'MetricItem';
